import {
  addDisplayName,
  addHandlers,
  addProps,
  addState,
  flowMax,
} from 'ad-hok'
import { FC } from 'react'
import { createUseStyles } from 'react-jss'
import { addApi, addUser } from 'shared/src/api'
import { UserInput } from 'shared/src/api/types'
import {
  addDataLoadingFromPromise,
  hasData,
} from 'shared/src/utils/dataLoading'

import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import { Button } from './Button'
import { ImageField } from './ImageField'
import { ResourceSelectField } from './ResourceSelectField'
import { TextArea, TextField } from './TextField'
import { Toggle } from './Toggle'
import { MAX_BIO_LENGTH } from './account'
import { CustomLinks } from './account/CustomLinks'
import { FormProps } from './resource'

const useStyles = createUseStyles({
  buttons: {
    display: 'flex',
    gap: 20,
  },
})

export interface FormValues
  extends Omit<
    Partial<UserInput>,
    | 'categoryIds'
    | 'isCreator'
    | 'isExcludedFromCreatorFund'
    | 'isFeatured'
    | 'isAssisted'
    | 'isVerified'
    | 'isDefaultFollow'
    | 'isAdmin'
    | 'isSuperAdmin'
    | 'isStealth'
    | 'showMerch'
    | 'shouldScrapeInstagram'
    | 'noSms'
    | 'defaultToLinksPage'
    | 'defaultToFullScreen'
  > {
  isCreator: boolean
  isExcludedFromCreatorFund: boolean
  isFeatured: boolean
  isAssisted: boolean
  isVerified: boolean
  isDefaultFollow: boolean
  isAdmin: boolean
  isSuperAdmin: boolean
  isStealth: boolean
  showMerch: boolean
  shouldScrapeInstagram: boolean
  noSms: boolean
  defaultToLinksPage: boolean
  defaultToFullScreen: boolean
  categoryId1?: number
  categoryId2?: number
  categoryId3?: number
}

export const UsersForm: FC<FormProps<FormValues>> = flowMax(
  addDisplayName('UsersForm'),
  addUser,
  addApi,
  addState('isTriggeringRefresh', 'setTriggeringRefresh', false),
  addHandlers({
    onOpenStats:
      ({ id }) =>
      () => {
        window.open(`/users/${id}/feed`, '_blank')
      },
    onOpenEdits:
      ({ id }) =>
      () => {
        window.open(`/users/${id}/userLists`, '_blank')
      },
    onOpenInsights:
      ({ id }) =>
      () => {
        window.open(`/users/${id}/insights`, '_blank')
      },
    onOpenCreatorEarnings:
      ({ id }) =>
      () => {
        window.open(`/users/${id}/earnings`, '_blank')
      },
    onTriggerSocialMediaRefresh:
      ({ id, api, setTriggeringRefresh }) =>
      async () => {
        if (!id) {
          return
        }

        setTriggeringRefresh(true)

        try {
          await api.users.triggerSocialMediaRefresh(id)
        } finally {
          setTriggeringRefresh(false)
        }
      },
  }),
  addDataLoadingFromPromise(
    'customLinks',
    ({ id, api }) =>
      id ? api.customLinks.getForUserId(id) : Promise.resolve([]),
    []
  ),
  addTranslationHelpers,
  addProps(() => ({
    classes: useStyles(),
  })),
  ({
    id,
    values,
    onChange,
    errors,
    customLinks,
    user,
    onOpenStats,
    onOpenEdits,
    onOpenInsights,
    onOpenCreatorEarnings,
    onTriggerSocialMediaRefresh,
    isTriggeringRefresh,
    t,
    classes,
  }) => (
    <div>
      <div className={classes.buttons}>
        <Button
          text={t('admin.resources.users.openMedia')}
          onClick={onOpenStats}
        />
        <Button
          text={t('admin.resources.users.openEdits')}
          onClick={onOpenEdits}
        />
        <Button
          text={t('admin.resources.users.openInsights')}
          onClick={onOpenInsights}
        />
        <Button
          text={t('admin.resources.users.openCreatorEarnings')}
          onClick={onOpenCreatorEarnings}
        />
      </div>
      <TextField
        id="email"
        label={t('admin.resources.users.fields.email')}
        value={values.email}
        onChange={(email) => onChange({ ...values, email })}
        error={errors.email}
        disabled={!user.isAdmin}
      />
      <TextField
        id="phoneNumber"
        label={t('admin.resources.users.fields.phoneNumber')}
        value={values.phoneNumber ?? ''}
        onChange={(phoneNumber) => onChange({ ...values, phoneNumber })}
        error={errors.phoneNumber}
      />
      {user.isAdmin && (
        <>
          <Button
            text={t('admin.resources.users.triggerSocialMediaRefresh')}
            onClick={onTriggerSocialMediaRefresh}
            disabled={isTriggeringRefresh}
          />
          <Toggle
            id="isCreator"
            label={t('admin.resources.users.fields.isCreator')}
            value={values.isCreator}
            onChange={(isCreator) => onChange({ ...values, isCreator })}
          />
          <Toggle
            id="inCreatorFund"
            label={t('admin.resources.users.fields.isExcludedFromCreatorFund')}
            value={values.isExcludedFromCreatorFund}
            onChange={(isExcludedFromCreatorFund) =>
              onChange({ ...values, isExcludedFromCreatorFund })
            }
          />
          <Toggle
            id="isFeatured"
            label={t('admin.resources.users.fields.isFeatured')}
            value={values.isFeatured}
            onChange={(isFeatured) => onChange({ ...values, isFeatured })}
          />
          <Toggle
            id="isAssisted"
            label={t('admin.resources.users.fields.isAssisted')}
            value={values.isAssisted}
            onChange={(isAssisted) => onChange({ ...values, isAssisted })}
          />
          <Toggle
            id="isVerified"
            label={t('admin.resources.users.fields.isVerified')}
            value={values.isVerified}
            onChange={(isVerified) => onChange({ ...values, isVerified })}
          />
          <Toggle
            id="isDefaultFollow"
            label={t('admin.resources.users.fields.isDefaultFollow')}
            value={values.isDefaultFollow}
            onChange={(isDefaultFollow) =>
              onChange({ ...values, isDefaultFollow })
            }
          />
          <Toggle
            id="isAdmin"
            label={t('admin.resources.users.fields.isAdmin')}
            value={values.isAdmin}
            onChange={(isAdmin) => onChange({ ...values, isAdmin })}
          />
          {user.isSuperAdmin && (
            <Toggle
              id="isSuperAdmin"
              label={t('admin.resources.users.fields.isSuperAdmin')}
              value={values.isSuperAdmin}
              onChange={(isSuperAdmin) => onChange({ ...values, isSuperAdmin })}
            />
          )}
          <Toggle
            id="isStealth"
            label={t('admin.resources.users.fields.isStealth')}
            value={values.isStealth}
            onChange={(isStealth) => onChange({ ...values, isStealth })}
          />
          <Toggle
            id="showMerch"
            label={t('admin.resources.users.fields.showMerch')}
            value={values.showMerch}
            onChange={(showMerch) => onChange({ ...values, showMerch })}
          />
          <Toggle
            id="shouldScrapeInstagram"
            label={t('admin.resources.users.fields.shouldScrapeInstagram')}
            value={values.shouldScrapeInstagram}
            onChange={(shouldScrapeInstagram) =>
              onChange({ ...values, shouldScrapeInstagram })
            }
          />
          <Toggle
            id="noSms"
            label={t('admin.resources.users.fields.noSms')}
            value={values.noSms}
            onChange={(noSms) => onChange({ ...values, noSms })}
          />
          <Toggle
            id="defaultToLinksPage"
            label={t('admin.resources.users.fields.defaultToLinksPage')}
            value={values.defaultToLinksPage}
            onChange={(defaultToLinksPage) =>
              onChange({ ...values, defaultToLinksPage })
            }
          />
          <Toggle
            id="defaultToFullScreen"
            label={t('admin.resources.users.fields.defaultToFullScreen')}
            value={values.defaultToFullScreen}
            onChange={(defaultToFullScreen) =>
              onChange({ ...values, defaultToFullScreen })
            }
          />
        </>
      )}
      <TextField
        id="username"
        label={t('admin.resources.users.fields.username')}
        value={values.username ?? ''}
        onChange={(username) => onChange({ ...values, username })}
        error={errors.username}
      />
      <ResourceSelectField
        id="brandId"
        label={t('admin.resources.users.fields.brand')}
        error={errors.brandId}
        resourceName="brands"
        selectedId={values.brandId ?? undefined}
        onChange={(brandId) => onChange({ ...values, brandId })}
        disableCreate
      />
      <TextArea
        id="bio"
        label={t('admin.resources.users.fields.bio')}
        value={values.bio ?? ''}
        onChange={(bio) =>
          onChange({ ...values, bio: bio.substring(0, MAX_BIO_LENGTH) })
        }
        error={errors.bio}
      />
      <ImageField
        id="imageId"
        label={t('admin.resources.users.fields.imageId')}
        value={values.imageId}
        onChange={(imageId) => onChange({ ...values, imageId })}
        error={errors.imageId}
      />
      <ImageField
        id="backgroundImageId"
        label={t('admin.resources.users.fields.backgroundImageId')}
        value={values.backgroundImageId}
        onChange={(backgroundImageId) =>
          onChange({ ...values, backgroundImageId })
        }
        error={errors.backgroundImageId}
      />
      <ResourceSelectField
        id="category1"
        label={t('admin.resources.users.fields.categories')}
        error={errors.categoryId1}
        resourceName="categories"
        selectedId={values.categoryId1}
        onChange={(categoryId1) => onChange({ ...values, categoryId1 })}
        disableCreate
      />
      <ResourceSelectField
        id="category2"
        error={errors.categoryId2}
        resourceName="categories"
        selectedId={values.categoryId2}
        onChange={(categoryId2) => onChange({ ...values, categoryId2 })}
        disableCreate
      />
      <ResourceSelectField
        id="category3"
        error={errors.categoryId3}
        resourceName="categories"
        selectedId={values.categoryId3}
        onChange={(categoryId3) => onChange({ ...values, categoryId3 })}
        disableCreate
      />
      {id && hasData(customLinks) && (
        <CustomLinks userId={id} initialLinks={customLinks.data} />
      )}
      <TextField
        id="homepage"
        label={t('admin.resources.users.fields.homepage')}
        value={values.homepage ?? ''}
        onChange={(homepage) => onChange({ ...values, homepage })}
        error={errors.homepage}
      />
      <TextField
        id="instagramUrl"
        label={t('admin.resources.users.fields.instagramUrl')}
        value={values.instagramUrl ?? ''}
        onChange={(instagramUrl) => onChange({ ...values, instagramUrl })}
        error={errors.instagramUrl}
      />
      <TextField
        id="pinterestUrl"
        label={t('admin.resources.users.fields.pinterestUrl')}
        value={values.pinterestUrl ?? ''}
        onChange={(pinterestUrl) => onChange({ ...values, pinterestUrl })}
        error={errors.pinterestUrl}
      />
      <TextField
        id="amazonUrl"
        label={t('admin.resources.users.fields.amazonUrl')}
        value={values.amazonUrl ?? ''}
        onChange={(amazonUrl) => onChange({ ...values, amazonUrl })}
        error={errors.amazonUrl}
      />
      <TextField
        id="tiktokUrl"
        label={t('admin.resources.users.fields.tiktokUrl')}
        value={values.tiktokUrl ?? ''}
        onChange={(tiktokUrl) => onChange({ ...values, tiktokUrl })}
        error={errors.tiktokUrl}
      />
      <TextField
        id="spotifyUrl"
        label={t('admin.resources.users.fields.spotifyUrl')}
        value={values.spotifyUrl ?? ''}
        onChange={(spotifyUrl) => onChange({ ...values, spotifyUrl })}
        error={errors.spotifyUrl}
      />
      <TextField
        id="goodreadsUrl"
        label={t('admin.resources.users.fields.goodreadsUrl')}
        value={values.goodreadsUrl ?? ''}
        onChange={(goodreadsUrl) => onChange({ ...values, goodreadsUrl })}
        error={errors.goodreadsUrl}
      />
      <TextField
        id="snapchatUrl"
        label={t('admin.resources.users.fields.snapchatUrl')}
        value={values.snapchatUrl ?? ''}
        onChange={(snapchatUrl) => onChange({ ...values, snapchatUrl })}
        error={errors.snapchatUrl}
      />
      <TextField
        id="etsyUrl"
        label={t('admin.resources.users.fields.etsyUrl')}
        value={values.etsyUrl ?? ''}
        onChange={(etsyUrl) => onChange({ ...values, etsyUrl })}
        error={errors.etsyUrl}
      />
      <TextField
        id="youtubeUrl"
        label={t('admin.resources.users.fields.youtubeUrl')}
        value={values.youtubeUrl ?? ''}
        onChange={(youtubeUrl) => onChange({ ...values, youtubeUrl })}
        error={errors.youtubeUrl}
      />
      <TextField
        id="twitterUrl"
        label={t('admin.resources.users.fields.twitterUrl')}
        value={values.twitterUrl ?? ''}
        onChange={(twitterUrl) => onChange({ ...values, twitterUrl })}
        error={errors.twitterUrl}
      />
      <TextField
        id="publicEmail"
        label={t('admin.resources.users.fields.publicEmail')}
        value={values.publicEmail ?? ''}
        onChange={(publicEmail) => onChange({ ...values, publicEmail })}
        error={errors.publicEmail}
      />
      <TextField
        id="facebookUrl"
        label={t('admin.resources.users.fields.facebookUrl')}
        value={values.facebookUrl ?? ''}
        onChange={(facebookUrl) => onChange({ ...values, facebookUrl })}
        error={errors.facebookUrl}
      />
      <TextField
        id="applePodcastsUrl"
        label={t('admin.resources.users.fields.applePodcastsUrl')}
        value={values.applePodcastsUrl ?? ''}
        onChange={(applePodcastsUrl) =>
          onChange({ ...values, applePodcastsUrl })
        }
        error={errors.applePodcastsUrl}
      />
    </div>
  )
)
